import {
  ChangeEventHandler,
  ComponentProps,
  FocusEventHandler,
  useCallback,
  useState,
} from 'react';
import Input from '../Input';

type Props = {
  value: number | null;
  /** 小数点以下の桁数 */
  digits: number;
  min?: number;
  max?: number;
  onChange?: (x: number | null) => void;
} & Pick<ComponentProps<typeof Input>, 'name' | 'variants'>;

function InputFixedNum({ value, digits, onChange, min, max, ...props }: Props) {
  const [focus, setFocus] = useState(false);

  const onFocus = useCallback(() => {
    setFocus(true);
  }, []);

  const onBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      const n = parseNum(e.target.value, { digits, min, max });

      if (Number.isNaN(n)) {
        e.target.value = '';
        onChange?.(null);
      } else {
        e.target.value = n.toFixed(digits);
        onChange?.(n);
      }

      setFocus(false);
    },
    [digits, onChange, min, max]
  );

  const onChangeRaw = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const n = parseNum(e.target.value, { digits, min, max });

      if (Number.isNaN(n)) {
        onChange?.(null);
      } else {
        onChange?.(n);
      }
    },
    [digits, onChange, min, max]
  );

  const override = focus
    ? undefined
    : value === null
    ? ''
    : value.toFixed(digits);

  return (
    <Input
      {...props}
      value={override}
      onChange={onChangeRaw}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
}

export default InputFixedNum;

function parseNum(
  value: string,
  {
    digits,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
  }: { digits: number; min?: number; max?: number }
): number {
  const s = value
    .trim()
    .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
  const n = Number(s || NaN);
  return Number(Math.max(min, Math.min(n, max)).toFixed(digits));
}
